import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const SalesChannelForm = ({
  channelFormData,
  onInputChange,
  onSubmit,
  isSubmitting,
  onCancel,
  isEditing = false,
  submitLabel,
}) => {
  return (
    <Box sx={{ p: 1 }}>
      {/* Header */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {isEditing ? 'Edit Sales Channel' : 'Create New Sales Channel'}
      </Typography>

      <form onSubmit={onSubmit}>
        <Stack spacing={3}>
          {/* Channel Name */}
          <TextField
            fullWidth
            label="Channel Name"
            name="name"
            autoComplete="off"
            value={channelFormData.name}
            onChange={onInputChange}
            required
            placeholder="Enter a channel name (e.g., eBay, Facebook Marketplace, local antique store, flea market, etc.)"
            variant="outlined"
          />

          {/* Default Location */}
          <TextField
            fullWidth
            label="Default Location"
            name="defaultLocation"
            value={channelFormData.defaultLocation || ''}
            onChange={onInputChange}
            placeholder="Default location for items in this channel (optional)"
            variant="outlined"
          />

          {/* Channel Link */}
          <TextField
            fullWidth
            label="Channel Link"
            name="channelLink"
            value={channelFormData.channelLink || ''}
            onChange={onInputChange}
            placeholder="https://www.ebay.com/str/yourstore"
            variant="outlined"
          />

          {/* QuailHQ Integration Settings */}
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            QuailHQ Barcode Integration
          </Typography>
          <FormControlLabel
            control={
              <Switch
                name="quailhqEnabled"
                checked={!!channelFormData.quailhqEnabled}
                onChange={(e) => onInputChange({ target: { name: 'quailhqEnabled', value: e.target.checked } })}
              />
            }
            label="Enable QuailHQ Integration"
          />
          {channelFormData.quailhqEnabled && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
              <TextField
                fullWidth
                label="QuailHQ Account ID"
                name="quailhqAccountId"
                value={channelFormData.quailhqAccountId || ''}
                onChange={onInputChange}
                required
                variant="outlined"
                helperText={
                  <>
                    Your Sandpiper account ID:{' '}
                    <a href="https://app.sandpiperhq.com" target="_blank" rel="noopener noreferrer">
                      app.sandpiperhq.com
                    </a>
                  </>
                }
              />
              <TextField
                fullWidth
                label="Booth Number"
                name="quailBoothNumber"
                value={channelFormData.quailBoothNumber || ''}
                onChange={onInputChange}
                required
                variant="outlined"
                placeholder="e.g., A-12"
                helperText="Your booth number used for QuailHQ barcode requests"
              />
            </Stack>
          )}

          {/* Item Number Settings */}
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Item Number & Label Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                name="useItemNumber"
                checked={!!channelFormData.useItemNumber}
                onChange={(e) => onInputChange({ target: { name: 'useItemNumber', value: e.target.checked } })}
              />
            }
            label="Use Item Numbers"
          />
          {channelFormData.useItemNumber && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Switch
                    name="useCustomItemNumber"
                    checked={!!channelFormData.useCustomItemNumber}
                    onChange={(e) => onInputChange({ target: { name: 'useCustomItemNumber', value: e.target.checked } })}
                  />
                }
                label="Use Custom Item Numbers"
              />
              {channelFormData.useCustomItemNumber ? (
                <Typography variant="body2" color="text.secondary">
                  Users will enter their own item numbers when creating inventory items.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Item Number Prefix"
                      name="itemNumberPrefix"
                      value={channelFormData.itemNumberPrefix || ''}
                      onChange={onInputChange}
                      placeholder="e.g., AM"
                      variant="outlined"
                      helperText="Optional prefix prepended to item numbers"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Item Number Start"
                      name="itemNumberStart"
                      value={channelFormData.itemNumberStart || ''}
                      onChange={onInputChange}
                      required
                      placeholder="e.g., 001"
                      variant="outlined"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      helperText="Starting sequence number (digits only)"
                    />
                  </Grid>
                </Grid>
              )}
            </Stack>
          )}

          {/* Label & Barcode Settings */}
          <FormControlLabel
            control={
              <Switch
                name="labelRequired"
                checked={!!channelFormData.labelRequired}
                onChange={(e) => onInputChange({ target: { name: 'labelRequired', value: e.target.checked } })}
              />
            }
            label="Label Required"
          />
          {channelFormData.labelRequired && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Switch
                    name="barcodeRequired"
                    checked={!!channelFormData.barcodeRequired}
                    onChange={(e) => onInputChange({ target: { name: 'barcodeRequired', value: e.target.checked } })}
                  />
                }
                label="Barcode Required"
              />
              {channelFormData.barcodeRequired && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Barcode Format"
                      name="barcodeFormat"
                      value={channelFormData.barcodeFormat || ''}
                      onChange={onInputChange}
                      required
                      variant="outlined"
                      helperText="Format used for generating barcodes"
                    >
                      <MenuItem value="">Select Format</MenuItem>
                      <MenuItem value="Code 128">Code 128</MenuItem>
                      <MenuItem value="QR Code">QR Code</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Barcode Source"
                      name="barcodeSource"
                      value={channelFormData.barcodeSource || 'itemNumber'}
                      variant="outlined"
                      disabled
                      helperText="Field used for barcode value (currently fixed to item number)"
                    />
                  </Grid>
                </Grid>
              )}
            </Stack>
          )}

          {/* Commission Settings */}
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Commission
          </Typography>
          <FormControlLabel
            control={
              <Switch
                name="chargesCommission"
                checked={!!channelFormData.chargesCommission}
                onChange={(e) => onInputChange({ target: { name: 'chargesCommission', value: e.target.checked } })}
              />
            }
            label="Charges Commission"
          />
          {channelFormData.chargesCommission && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
              <TextField
                fullWidth
                label="Commission Percentage (%)"
                name="commissionPercentage"
                type="number"
                value={channelFormData.commissionPercentage}
                onChange={onInputChange}
                required
                variant="outlined"
                inputProps={{ min: 1, max: 100, step: 0.01, inputMode: 'decimal' }}
                helperText="New rate applies to future sales only."
              />
            </Stack>
          )}

          {/* Form Actions */}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={<SaveIcon />}
            >
              {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (submitLabel || (isEditing ? 'Update Channel' : 'Create Channel'))}
            </Button>
            {onCancel && (
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                onClick={onCancel}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default SalesChannelForm;
